import { PaginatedResponse, WithListParams } from '@/types/pagination'
import { ApiFeatureUsageSummary } from '@/types/subscription'
import {
  MemberInvitation,
  Workspace,
  WorkspaceApiKey,
  WorkspaceApiKeyScope,
  WorkspaceMember,
  WorkspaceMemberRole,
  WorkspaceMemberStatus
} from '@/types/workspace'
import { vpApi } from './vpApi'

type CreateWorkspacePayload = Pick<
  Workspace,
  'name' | 'workspace_type' | 'require_mfa'
>

const createWorkspace = async (payload: CreateWorkspacePayload) => {
  const res = await vpApi.post<Workspace>('/v1/workspaces/', payload)
  return res.data
}

const getUserWorkspace = async (workspaceId: string) => {
  const res = await vpApi.get<Workspace>(`/v1/workspaces/${workspaceId}/`)
  return res.data
}

type UpdateWorkspaceParams = {
  workspaceId: string
  payload: Partial<Workspace>
}

const updateWorkspace = async ({
  workspaceId,
  payload
}: UpdateWorkspaceParams) => {
  return vpApi.patch(`/v1/workspaces/${workspaceId}/`, payload)
}

export type GetWorkspaceMembersParams = WithListParams<{
  status?: WorkspaceMemberStatus
}>

const getWorkspaceMembers = async (
  workspaceId: string,
  params?: GetWorkspaceMembersParams
) => {
  const res = await vpApi.get<PaginatedResponse<WorkspaceMember[]>>(
    `/v1/workspaces/${workspaceId}/members/`,
    { params }
  )
  return res.data
}

type InviteMembersParams = {
  workspaceId: string
  members: MemberInvitation[]
}

const inviteMembers = async ({ workspaceId, members }: InviteMembersParams) => {
  return vpApi.post(`/v1/workspaces/${workspaceId}/invite/`, members)
}

type UpdateMemberParams = {
  workspaceId: string
  memberId: string
  role: WorkspaceMemberRole
}

const updateWorkspaceMember = ({
  workspaceId,
  memberId,
  role
}: UpdateMemberParams) => {
  return vpApi.patch(`/v1/workspaces/${workspaceId}/members/${memberId}/`, {
    role
  })
}

type ResendInviteParams = {
  workspaceId: string
  memberId: string
}

const resendInvite = ({ workspaceId, memberId }: ResendInviteParams) => {
  return vpApi.post(
    `/v1/workspaces/${workspaceId}/members/${memberId}/resend-invite/`
  )
}

type DeactivateMemberParams = {
  workspaceId: string
  memberId: string
}

const deactivateWorkspaceMember = async ({
  workspaceId,
  memberId
}: DeactivateMemberParams) => {
  return await vpApi.post(
    `/v1/workspaces/${workspaceId}/members/${memberId}/deactivate/`
  )
}

type AcceptWorkspaceInvitationParams = {
  workspaceId: string
  token: string
}

const acceptWorkspaceInvitation = ({
  workspaceId,
  token
}: AcceptWorkspaceInvitationParams) => {
  return vpApi.post(`/v1/workspaces/${workspaceId}/accept-invite/`, { token })
}

const getMyMembership = async (workspaceId: string) => {
  const res = await vpApi.get<WorkspaceMember>(
    `/v1/workspaces/${workspaceId}/my-membership/`
  )
  return res.data
}

const getWorkspaceUsage = async () => {
  const res = await vpApi.get<ApiFeatureUsageSummary>(`/v1/workspaces/usage/`)
  return res.data
}

const selectWorkspace = (workspaceId: string) => {
  return vpApi.put(`/v1/workspaces/${workspaceId}/select/`)
}

export type GetWorkspaceApiKeysParams = WithListParams

const getWorkspaceApiKeys = async (params?: GetWorkspaceApiKeysParams) => {
  const res = await vpApi.get<PaginatedResponse<WorkspaceApiKey[]>>(
    '/v1/workspaces/api-keys/',
    { params }
  )
  return res.data
}

type CreateApiKeyPayload = {
  name: string
  scope: WorkspaceApiKeyScope
  application_ids: string[]
}

type CreateApiKeyResponse = WorkspaceApiKey & {
  /** Unmasked key value, only available once after key creation */
  key: string
}

const createApiKey = async (payload: CreateApiKeyPayload) => {
  const res = await vpApi.post<CreateApiKeyResponse>(
    '/v1/workspaces/api-keys/',
    payload
  )
  return res.data
}

const deleteApiKey = async (apiKeyId: string) => {
  return vpApi.delete(`/v1/workspaces/api-keys/${apiKeyId}/`)
}

export const workspaceApi = {
  createWorkspace,
  getUserWorkspace,
  updateWorkspace,
  getWorkspaceMembers,
  getMyMembership,
  getWorkspaceUsage,
  inviteMembers,
  updateWorkspaceMember,
  resendInvite,
  deactivateWorkspaceMember,
  acceptWorkspaceInvitation,
  selectWorkspace,
  getWorkspaceApiKeys,
  createApiKey,
  deleteApiKey
}
